import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useVenue } from "../VenueProvider";
import { Section, formatMXN, CustomTooltip, ACCENT } from "../cuentas/shared";
import { Kpi, fmtPct, ultimosMeses, labelMes, VALOR_COLOR, COSTO_COLOR } from "./shared";
import {
  variacion,
  resumenPlanners,
  comisionesPorMes,
} from "../../utils/reportes";
import { getMonthRange } from "../../utils/dates";
import { describeAcuerdo, acuerdoDe } from "../../utils/comisiones";
import {
  UserGroupIcon,
  CalendarDaysIcon,
  TableCellsIcon,
} from "@heroicons/react/20/solid";

// Un planner no cobra tarifa, cobra comisión (FRA-355): la pregunta no es
// cuánto le pagaste sino cuánto negocio te trajo y a qué costo.
//
// Dos anclajes distintos y a propósito: el negocio se cuenta por la fecha
// del EVENTO y la comisión por la fecha del COSTO. Un evento de enero con
// comisión capturada en diciembre aparece en los dos meses, cada cifra en
// el suyo — mezclarlos haría que la tasa efectiva mintiera.
const ReportePlanners = ({ rango }) => {
  const navigate = useNavigate();
  const { proveedores, costos, events, calendarTimezone: tz } = useVenue();

  const actual = useMemo(
    () =>
      resumenPlanners({
        proveedores,
        costos,
        events,
        desde: rango.from,
        hasta: rango.to,
        timeZone: tz,
      }),
    [proveedores, costos, events, rango, tz]
  );
  const previo = useMemo(
    () =>
      rango.prev
        ? resumenPlanners({
            proveedores,
            costos,
            events,
            desde: rango.prev.from,
            hasta: rango.prev.to,
            timeZone: tz,
          })
        : null,
    [proveedores, costos, events, rango, tz]
  );

  const serie = useMemo(
    () =>
      comisionesPorMes({
        meses: ultimosMeses(12),
        costos,
        timeZone: tz,
        getMonthRange,
      }).map((m) => ({
        mes: labelMes(m.mes),
        Pagadas: m.pagado,
        "Por pagar": m.porPagar,
      })),
    [costos, tz]
  );

  const delta = (campo) =>
    previo ? variacion(actual.totales[campo], previo.totales[campo]) : null;

  const proveedoresPorId = useMemo(() => {
    const map = {};
    (proveedores || []).forEach((p) => {
      if (p?.id) map[p.id] = p;
    });
    return map;
  }, [proveedores]);

  if (actual.filas.length === 0) {
    return (
      <Section icon={UserGroupIcon} title="Planners y comisiones">
        <p className="text-sm text-zinc-400">
          Todavía no tienes planners en el directorio. Se dan de alta en
          Directorio → Planners, con su acuerdo de comisión (monto fijo o
          porcentaje del valor del evento); al ligar uno a un evento, el panel
          te propone la comisión que toca.
        </p>
      </Section>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi
          label="Planners con evento"
          value={actual.totales.conEventoEnPeriodo}
          delta={delta("conEventoEnPeriodo")}
          hint={`de ${actual.filas.length} en el directorio`}
        />
        <Kpi
          label="Negocio que trajeron"
          value={formatMXN(actual.totales.valorPeriodo)}
          delta={delta("valorPeriodo")}
          hint={`${actual.totales.eventosPeriodo} ${
            actual.totales.eventosPeriodo === 1 ? "evento" : "eventos"
          } del período`}
        />
        <Kpi
          label="Comisiones"
          value={formatMXN(actual.totales.comisionTotal)}
          delta={delta("comisionTotal")}
          invertir
          hint={`${formatMXN(actual.totales.comisionPorPagar)} sin pagar`}
        />
        <Kpi
          label="Comisión efectiva"
          value={fmtPct(actual.totales.tasaEfectiva)}
          hint="sobre el valor que trajeron"
        />
        <Kpi
          label="Te quedó"
          value={formatMXN(actual.totales.valorPeriodo - actual.totales.comisionTotal)}
          hint="antes de los demás costos del evento"
          tone="text-emerald-600"
        />
      </div>

      <Section icon={TableCellsIcon} title="Planner por planner">
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
                <th className="py-2 pl-1 font-semibold">Planner</th>
                <th className="py-2 font-semibold">Acuerdo</th>
                <th className="py-2 font-semibold text-right">Eventos</th>
                <th className="py-2 font-semibold text-right">Trajo</th>
                <th className="py-2 font-semibold text-right">Comisión</th>
                <th className="py-2 font-semibold text-right">Efectiva</th>
                <th className="py-2 pr-1 font-semibold text-right">Te quedó</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/50">
              {actual.filas.map((f) => (
                <tr key={f.id}>
                  <td className="py-2 pl-1">
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/proveedores/${f.id}`)}
                      className="hover:underline text-left truncate max-w-[160px]"
                      style={{ color: ACCENT }}
                      title={f.nombre}
                    >
                      {f.nombre}
                    </button>
                  </td>
                  <td className="py-2 text-xs text-zinc-500 whitespace-nowrap">
                    {describeAcuerdo(acuerdoDe(proveedoresPorId[f.id]))}
                  </td>
                  <td className="py-2 text-right text-zinc-500 whitespace-nowrap">
                    {f.eventosPeriodo}
                    {f.eventos > f.eventosPeriodo && (
                      <span className="ml-1 text-xs text-zinc-400">
                        / {f.eventos}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                    {formatMXN(f.valorPeriodo)}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap text-red-600">
                    {formatMXN(f.comisionTotal)}
                    {f.comisionPorPagar > 0 && (
                      <span className="ml-1 text-xs text-amber-600">
                        ({formatMXN(f.comisionPorPagar)} por pagar)
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right text-zinc-500 whitespace-nowrap">
                    {fmtPct(f.tasaEfectiva)}
                  </td>
                  <td className="py-2 pr-1 text-right font-medium whitespace-nowrap text-emerald-600">
                    {formatMXN(f.valorNeto)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-zinc-400">
          Los eventos se cuentan por su fecha y las comisiones por la fecha del
          costo, así que un evento de un mes puede traer comisión de otro. La
          columna "Eventos" muestra los del período y, después de la diagonal,
          todos los que te ha traído.
        </p>
      </Section>

      <Section icon={CalendarDaysIcon} title="Comisiones mes a mes">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={serie} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatMXN(v)} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Pagadas" stackId="a" fill={COSTO_COLOR} />
            <Bar dataKey="Por pagar" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <p className="mt-2 text-xs text-zinc-400">
          Doce meses fijos, no el período de arriba:{" "}
          <span style={{ color: VALOR_COLOR }}>una comisión se juzga contra su historia</span>,
          no contra el mes de junto.
        </p>
      </Section>
    </div>
  );
};

export default ReportePlanners;
